import { useMemo, useCallback } from "react";
import {
  useExternalStoreRuntime,
  type ThreadMessageLike,
  type AppendMessage,
  type ExternalStoreAdapter,
} from "@assistant-ui/react";
import type { ChatMessage } from "../types/assistant";

type UseAssistantRuntimeOptions = {
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  isRunning: boolean;
  sendMessage: (text: string, imageUrls?: string[]) => void;
  cancel: () => void;
};

/**
 * Bridge between our WebSocket state and assistant-ui's runtime
 */
export function useAssistantRuntime({
  messages,
  setMessages,
  isRunning,
  sendMessage,
  cancel,
}: UseAssistantRuntimeOptions) {
  // Convert our messages to assistant-ui format
  const convertMessage = useCallback(
    (message: ChatMessage): ThreadMessageLike => {
      const base = {
        id: message.id,
        role: message.role,
        content: [{ type: "text" as const, text: message.content }],
        createdAt: message.createdAt,
      };

      // Status is only supported for assistant messages
      if (message.role === "assistant") {
        return {
          ...base,
          status:
            message.status === "completed"
              ? { type: "complete" as const, reason: "stop" as const }
              : message.status === "in_progress"
                ? { type: "running" as const }
                : message.status === "error"
                  ? { type: "incomplete" as const, reason: "error" as const }
                  : { type: "incomplete" as const, reason: "other" as const },
        };
      }

      return base;
    },
    []
  );

  // Handle new message from user
  const onNew = useCallback(
    async (message: AppendMessage) => {
      if (message.content.length === 0) return;

      // Extract text content
      const textParts = message.content.filter((part) => part.type === "text");
      const text = textParts.map((part) => part.text).join("\n");

      if (!text.trim()) return;

      // Extract image URLs if any
      const imageParts = message.content.filter((part) => part.type === "image");
      const imageUrls = imageParts
        .map((part) => (part as { type: "image"; image: string }).image)
        .filter(Boolean);

      sendMessage(text, imageUrls.length > 0 ? imageUrls : undefined);
    },
    [sendMessage]
  );

  // Handle cancel
  const onCancel = useCallback(async () => {
    cancel();
  }, [cancel]);

  // Handle reload (regenerate last response)
  const onReload = useCallback(
    async (_parentId: string | null) => {
      // Find the last user message and resend it
      const lastUserMessage = [...messages]
        .reverse()
        .find((m) => m.role === "user");
      if (lastUserMessage) {
        // Remove the last assistant message(s) after this user message
        const userIndex = messages.findIndex((m) => m.id === lastUserMessage.id);
        setMessages(messages.slice(0, userIndex + 1));
        sendMessage(lastUserMessage.content);
      }
    },
    [messages, setMessages, sendMessage]
  );

  const adapter: ExternalStoreAdapter<ChatMessage> = useMemo(
    () => ({
      messages,
      isRunning,
      convertMessage,
      onNew,
      onCancel,
      onReload,
    }),
    [messages, isRunning, convertMessage, onNew, onCancel, onReload]
  );

  return useExternalStoreRuntime(adapter);
}
